<template>
  <SoDialog
    v-model="value"
    :loading="isSaving"
    card-title="Are you sure?"
    data-testid="features-modal"
  >
    <SoErrorBoundary>
      <SoAlert
        type="warning"
        title="You are about to make the following changes:"
      />
      <SoSimpleTable
        :headers="['Feature', 'Enabled']"
        :items="features.map((ft) => [ft.name, ft.enabled])"
        class="mt-4 mb-2 border rounded"
        data-testid="features-modal-table"
      />
    </SoErrorBoundary>
    <SoAlert
      v-if="saveSucceeded"
      title="Changes saved"
      type="success"
      class="mt-4"
      data-testid="feature-modal-success-alert"
    />
    <SoAlert
      v-if="saveError"
      title="Failed to save changes"
      type="error"
      class="mt-4"
      data-testid="feature-modal-error-alert"
    />
    <SoCheckbox
      v-if="isProduction && !saveSucceeded"
      v-model="confirmed"
      label="I understand these changes will affect our customers"
      class="mt-6"
      data-testid="feature-modal-confirm-checkbox"
    />
    <div class="d-flex justify-end mt-8">
      <template v-if="saveSucceeded">
        <SoButton
          title="Close"
          large
          data-testid="feature-modal-close-btn"
          @click="value = false"
        />
      </template>
      <template v-else>
        <SoButton
          title="Cancel"
          class="mr-2"
          large
          data-testid="feature-modal-cancel-btn"
          @click="value = false"
        />
        <SoButton
          title="Save"
          type="success"
          large
          :disabled="isProduction && !confirmed"
          data-testid="feature-modal-save-btn"
          @click="saveChanges()"
        />
      </template>
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { FeatureMutator } from "@/mutators/feature/FeatureMutator"
  import { Feature } from "@/models/admin/feature/feature"
  import SoDialog from "@/components/SoDialog.vue"
  import SoErrorBoundary from "@/components/SoErrorBoundary.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { isProduction } from "@/helpers/envHelper"

  export default defineComponent({
    components: {
      SoDialog,
      SoErrorBoundary,
      SoSimpleTable,
      SoButton,
      SoCheckbox,
      SoAlert,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      features: {
        type: Array as PropType<Feature[]>,
        default: null,
      },
    },
    emits: ["update:modelValue", "success"],
    data() {
      return {
        toggleFeatureMutator: FeatureMutator.update(),
        isSaving: false,
        saveSucceeded: false,
        saveError: false,
        confirmed: false,
      }
    },
    computed: {
      isProduction,
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
    },
    watch: {
      value(val) {
        if (!val) {
          if (this.saveSucceeded) {
            this.$emit("success")
          }
          this.saveSucceeded = false
          this.saveError = false
          this.confirmed = false
        }
      },
    },
    methods: {
      async saveChanges() {
        this.isSaving = true
        this.saveError = false
        try {
          await Promise.all(
            this.features.map((ft) =>
              this.toggleFeatureMutator.mutateAsync({
                id: ft.id,
                enabled: ft.enabled,
              }),
            ),
          )
          this.saveSucceeded = true
        } catch {
          this.saveError = true
        } finally {
          this.isSaving = false
        }
      },
    },
  })
</script>
